<script setup lang="ts">
import type { SurroundingNeighborhoodsProps } from './index';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { ResultTuple } from '~~/core/helpers/result';
import type {
	GetReportApiReturn,
	RegionReport
} from '~~/core/ports/repositories';

import { cn } from '@/utils';
import { StandardSlider } from '#components';
import { useEvents } from '~/composables/events/useEvents';
import {
	PropertyTypeEnum,
	PropertyTypePlurals
} from '~/core/entities/CRMEnums';
import { MixpanelProps } from '~/core/service/mixpanel';
import { Result } from '~~/core/helpers/result';

import RegionCard from '~/components/CuratedListSimilarRegions/RegionCard.vue';

const props = defineProps<SurroundingNeighborhoodsProps>();

const SECTION_NAME = 'neighborhoods';
const { trackCardNeighborhoodClicked } = useEvents();

const emit = defineEmits<{
	(e: 'filter-link-clicked', filters: PropertyFilters): void;
}>();

const key = computed(() => {
	const normalizedRegion = normalizeString(props.region);
	const normalizedCity = normalizeString(props.city);
	const normalizedState = normalizeString(props.state);
	const normalizedPropertyType = normalizeString(props.propertyType);
	const neighborhoodKey = [
		normalizedRegion,
		normalizedCity,
		normalizedState,
		normalizedPropertyType
	]
		.filter(Boolean)
		.join('-');

	return `listing:surrounding-neighborhoods-${neighborhoodKey}`;
});

const defaultReports = { payload: [] as RegionReport[] };

async function fetchReports(): Promise<
	ResultTuple<GetReportApiReturn['data']>
> {
	try {
		const propertyTypeQuery = props.propertyType
			? { propertyType: [props.propertyType] }
			: {};
		const locationQuery = {
			...(props.city ? { city: props.city } : {}),
			...(props.state ? { state: props.state } : {})
		};

		const response = await $fetch<GetReportApiReturn['data']>(
			`/api/region/report`,
			{
				method: 'GET',
				query: {
					referenceRegion: props.region,
					...locationQuery,
					includeTypologiesFeatures: !!props.propertyType,
					...propertyTypeQuery
				}
			}
		);

		return Result.ok(response);
	} catch (error: any) {
		return Result.error(error);
	}
}

const {
	data: reports,
	status: status,
	execute
} = useAsyncData(
	key.value,
	async () => {
		try {
			if (!props.region) {
				return defaultReports;
			}

			const [result, error] = await fetchReports();

			if (error || !result?.length) {
				return defaultReports;
			}

			return {
				payload: result
			};
		} catch {
			return defaultReports;
		}
	},
	{
		default: () => defaultReports,
		lazy: true
	}
);

const filteredReports = computed(() => {
	if (!props.propertyType) {
		return reports.value.payload;
	}

	return reports.value.payload.filter(report =>
		report.typologies?.some(typo => typo.propertyType === props.propertyType)
	);
});

const isLoadingReports = computed(() => {
	return status.value === 'pending';
});

const title = computed<string>(() => {
	const base = `em bairros próximos a ${props.region}`;
	const preposition = props.propertyType
		? PropertyTypePlurals[props.propertyType as PropertyTypeEnum]
		: 'Imóveis';

	return `${preposition} ${base}`;
});

const isEmpty = computed(() => {
	return reports.value.payload.length === 0;
});

function handleRegionClicked(
	payload: { neighborhood_name: string; destination_url: string },
	index: number
) {
	trackCardNeighborhoodClicked({
		[MixpanelProps.CURRENT_PAGE]: 'Listing Properties',
		[MixpanelProps.SECTION_NAME]: SECTION_NAME,
		[MixpanelProps.CARD_POSITION]: index + 1,
		[MixpanelProps.DESTINATION_URL]: payload.destination_url,
		[MixpanelProps.NEIGHBORHOOD_NAME]: payload.neighborhood_name
	});
}

function handleRegionCardClicked(payload: {
	filters: PropertyFilters;
	destinationUrl: string;
}) {
	emit('filter-link-clicked', payload.filters);
}

watch(
	() => [props.propertyType, props.region, props.city, props.state],
	() => {
		execute();
	}
);
</script>

<template>
	<StandardSlider
		v-show="!isEmpty || isLoadingReports"
		:items="filteredReports"
		:title="title"
		:is-loading="isLoadingReports"
		:section-name="SECTION_NAME"
		carousel-item-size="half-responsive"
		title-as="h2"
		title-classes="md:text-[32px]"
		:container-class="cn(containerClass, '!px-0')"
	>
		<template #items="{ item, index }">
			<RegionCard
				:city="item.city"
				:state="item.state"
				:region="item.region"
				:property-count="item.propertyCount"
				:top-property-types="item.topPropertyTypes"
				:typologies="item.typologies || []"
				:selected-property-type="props.propertyType"
				@region-clicked="payload => handleRegionClicked(payload, index)"
				@region-card-clicked="payload => handleRegionCardClicked(payload)"
			/>
		</template>
		<template #cta></template>
	</StandardSlider>
</template>
